import { forwardRef } from 'react';
import { Button } from '@/components/ui/Button';
import { ReferenceVideoCard } from '@/features/feedback/components/ReferenceVideoCard';
import { readReferenceVideo } from '@/features/matches/referenceVideo';
import type { FeedbackAnsweredContextResponse, FeedbackConversationResponse, FeedbackFullViewResponse, ResourcePin } from '@/lib/types';
import { FeedbackPinnedResources } from '@/features/feedback-integration/FeedbackPinnedResources';
import type { FeedbackPinSource } from '@/features/feedback-integration/useFeedbackPinRead';

type Content = FeedbackConversationResponse | FeedbackFullViewResponse | FeedbackAnsweredContextResponse;
export const FeedbackConversationContext = forwardRef<HTMLElement, {
  content: Content; historical: boolean; onCurrent: () => void; source: FeedbackPinSource; onPreview?: (pin: ResourcePin) => void;
}>(function FeedbackConversationContext({ content, historical, onCurrent, source, onPreview }, ref) {
  if ('availability' in content && content.availability === 'unavailable') return <section ref={ref} className="space-y-3 p-4">
    <p role="status">The Feedback answered by this reply is no longer available.</p>
    <Button variant="outline" onClick={onCurrent}>Read current Feedback</Button>
  </section>;
  const external = content.external_reference ? readReferenceVideo(content.external_reference) : null;
  return <section ref={ref} className="space-y-5 break-words" aria-label={historical ? 'Feedback answered by this reply' : 'Current coach Feedback'}>
    {historical && <div className="rounded-inset border border-rule bg-field p-3 text-sm">
      <p>Reply to Feedback version {content.feedback_version}.</p>
      <Button size="sm" variant="outline" onClick={onCurrent}>Read current Feedback</Button>
    </div>}
    <div className="flex flex-wrap items-start justify-between gap-2 text-xs text-ink-muted">
      <strong className="text-ink">{content.creator_label}</strong><span>Shared Feedback · version {content.feedback_version}</span>
    </div>
    <p className="whitespace-pre-wrap text-sm leading-relaxed">{content.message}</p>
    {content.next_step && <div className="rounded-inset bg-field p-3 text-sm"><strong>Next step</strong><p className="whitespace-pre-wrap">{content.next_step}</p></div>}
    <FeedbackPinnedResources resources={content.resource_links} source={source} onPreview={onPreview} />
    {external && <ReferenceVideoCard video={external} label="External video" />}
    {content.goals.length > 0 && <section className="space-y-2" aria-label="Linked goals"><h4 className="text-sm font-semibold">Linked goals</h4>
      {content.goals.map(goal => <div key={goal.goal_id} className="rounded-inset border border-rule p-3 text-sm">
        <strong>{goal.title}</strong><span className="ml-2 text-xs text-ink-muted">{goal.status}</span>
      </div>)}
    </section>}
  </section>;
});
